import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import { adminApi } from "../axios"
import AdminEntityAutocomplete from "./AdminEntityAutocomplete"

function assignmentError(error) {
  const data = error?.response?.data
  return (
    data?.errors?.[0]?.error ||
    data?.detail ||
    data?.concierge_user_id ||
    "Could not create assignment."
  )
}

/** Assign a concierge to one parent account, straight from the Users list. */
export default function AdminAssignConciergeDialog({
  open,
  owner,
  onClose,
  onAssigned,
}) {
  const [concierge, setConcierge] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setConcierge(null)
      setError(null)
    }
  }, [open])

  async function handleAssign() {
    if (!concierge || !owner) return
    setSaving(true)
    setError(null)
    try {
      const { data } = await adminApi.createAssignment({
        concierge_user_id: concierge.id,
        owner_user_ids: [owner.id],
      })
      if (data?.errors?.length) {
        setError(data.errors[0]?.error || "Could not create assignment.")
        return
      }
      onAssigned?.()
    } catch (err) {
      setError(assignmentError(err))
    } finally {
      setSaving(false)
    }
  }

  const current = owner?.assigned_concierges || []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign concierge</DialogTitle>
      <DialogContent>
        <Column gap={2} sx={{ mt: 1 }}>
          <DialogContentText>
            Parent account: <strong>{owner?.name || owner?.email}</strong>
            {current.length
              ? `. Already assigned: ${current
                  .map((item) => item.name || item.email)
                  .join(", ")}.`
              : ""}
          </DialogContentText>
          <AdminEntityAutocomplete
            entity="concierge"
            size="medium"
            label="Concierge"
            placeholder="Search active concierges"
            value={concierge}
            onChange={setConcierge}
          />
          {error ? <Alert severity="error">{String(error)}</Alert> : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!concierge || saving}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  )
}

AdminAssignConciergeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  owner: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onAssigned: PropTypes.func,
}
